import { useState, useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 React routes
import adminRoutes, { superAdminRoute, authenticationRoute } from "routes";

// Legal Pages (Standalone & Public)

import TermsAndConditions from "layouts/legal/TermsAndConditions";

import PrivacyPolicy from "layouts/legal/PrivacyPolicy";

// Material Dashboard 2 React contexts
import { useMaterialUIController } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

// Sessions
import Session from "utils/Sessions";

// JWT decode
import jwtDecode from "jwt-decode";
import { useDispatch } from "react-redux";

// Constants
import { defaultData } from "utils/Constants";
import { updateRole } from "redux/Slice/Authentication";

export default function App() {
  const [controller] = useMaterialUIController();
  const { layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } = controller;
  const [role, setRole] = useState("");
  const { pathname } = useLocation();

  const isLegalPage =
    pathname.includes("terms-and-conditions") || pathname.includes("privacy-policy");
  const [isSuperAdminViewingAdminPanel, setIsSuperAdminViewingAdminPanel] = useState(false);
  const dispatch = useDispatch();

  // updating role based on token  and Setting page scroll to 0 when changing the route
  useEffect(() => {
    if (pathname.includes("authentication/reset-password")) {
      Session.setClear();
      setRole(defaultData.UNAUTHORIZED_ROLE);
    } else if (Session.userToken) {
      const token = jwtDecode(Session.userToken);
      setRole(token.role);
      Session.setUserRole(token.role);
      setIsSuperAdminViewingAdminPanel(Session.isSuperAdminViewingAdminPanel);
      dispatch(updateRole(token.role));
    } else {
      setRole(defaultData.UNAUTHORIZED_ROLE);
    }
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname, Session.userToken, Session.isSuperAdminViewingAdminPanel]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  if (role === "") {
    return null;
  }

  const roleLower = (role || "").toLowerCase();
  const isAdmin =
    roleLower === (defaultData.ADMIN_ROLE || "admin").toLowerCase() ||
    roleLower === (defaultData.SUPER_ADMIN_ROLE || "superadmin").toLowerCase();

  let activeRoutes = adminRoutes;
  if (isAdmin && !isSuperAdminViewingAdminPanel) {
    activeRoutes = superAdminRoute;
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" &&
        role !== "" &&
        role !== defaultData.UNAUTHORIZED_ROLE &&
        !isLegalPage && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Reynard"
              routes={activeRoutes}
              role={role}
            />
            <Configurator />
          </>
        )}
      {layout === "vr" && <Configurator />}
      <Routes>
        <Route exact path="/terms-and-conditions" element={<TermsAndConditions />} />

        <Route exact path="/privacy-policy" element={<PrivacyPolicy />} />
        {getRoutes(
          (isAdmin && activeRoutes) ||
            (roleLower === (defaultData.PARTNER_ROLE || "partner").toLowerCase() && adminRoutes) ||
            (roleLower === (defaultData.UNAUTHORIZED_ROLE || "unauthorized").toLowerCase() &&
              authenticationRoute)
        )}
        <Route
          path="*"
          element={
            <Navigate
              to={
                ((isAdmin || roleLower === (defaultData.PARTNER_ROLE || "partner").toLowerCase()) &&
                  "client/lead-bay") ||
                (roleLower === (defaultData.UNAUTHORIZED_ROLE || "unauthorized").toLowerCase() &&
                  "/authentication/sign-in")
              }
            />
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
